import { zodResolver } from '@hookform/resolvers/zod';
import { parse } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { PhoneNumber } from 'react-phone-number-input';
import { parsePhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import { useCustomer } from '@/api/customers/use-customer';
import { useCustomerStartOtp } from '@/api/customers/use-customer-start-otp';
import { useUpdateCustomer } from '@/api/customers/use-update-customer';
import type { PhoneCountry } from '@/components/input-phone/types';
import { validatePhoneNumber } from '@/core';

const formatDob = (text: string) => {
  const digits = text.replace(/\D/g, '').slice(0, 8);

  let formatted = digits.slice(0, 2);
  if (digits.length >= 2) formatted += '/';
  formatted += digits.slice(2, 4);
  if (digits.length >= 4) formatted += '/';
  formatted += digits.slice(4, 8);

  return formatted;
};

export type FinishProfileFormType = {
  firstName: string;
  lastName: string;
  email: string;
  dob: string;
};

interface UseFinishProfileFormOptions {
  requiresDob?: boolean;
}

interface SubmitProfileOptions {
  smsOptIn?: boolean;
  onDone: (newPhone?: string) => void;
  onError: (message: string) => void;
}

export function useFinishProfileForm({
  requiresDob = true,
}: UseFinishProfileFormOptions = {}) {
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<PhoneCountry>({ callingCode: '1' });
  const dobPreviousRef = useRef('');

  const { data: customer } = useCustomer();
  const { mutate: updateCustomer, isPending: isUpdatingCustomer } =
    useUpdateCustomer();
  const { mutate: startCustomerOtp, isPending: isStartingOtp } =
    useCustomerStartOtp();

  const phoneKnown = Boolean(customer?.phone);

  const schema = useMemo(
    () =>
      z.object({
        firstName: z.string().min(2, 'Minimum 2 characters'),
        lastName: z.string().min(2, 'Minimum 2 characters'),
        email: z.string().email(),
        dob: requiresDob
          ? z
              .string()
              .regex(
                /^\d{2}\/\d{2}\/\d{4}$/,
                'Date of birth must be in the format MM/DD/YYYY'
              )
          : z.string().optional(),
      }),
    [requiresDob]
  );

  const {
    control,
    trigger,
    setValue,
    reset,
    handleSubmit,
    formState: { isValid },
  } = useForm<FinishProfileFormType>({
    defaultValues: {
      firstName: customer?.firstname ?? '',
      lastName: customer?.lastname ?? '',
      email: customer?.email ?? '',
      dob: customer?.dob ?? '',
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (customer) {
      reset();
      setValue('firstName', customer.firstname);
      setValue('lastName', customer.lastname);
      setValue('email', customer.email);
      setValue('dob', customer.dob ?? '');
      dobPreviousRef.current = customer.dob ?? '';
    }
  }, [customer, reset, setValue]);

  const handleDobChange = (text: string) => {
    const previous = dobPreviousRef.current;
    // Backspacing right after an auto-inserted "/" would otherwise just
    // re-add the same slash (since the digit count hasn't changed) and
    // feel stuck, so drop the digit before it too.
    const isDeletingSeparator =
      previous.endsWith('/') &&
      text.length === previous.length - 1 &&
      text === previous.slice(0, -1);

    const formatted = formatDob(isDeletingSeparator ? text.slice(0, -1) : text);

    dobPreviousRef.current = formatted;
    setValue('dob', formatted);
    trigger('dob');
  };

  const parsedExistingPhone = useMemo((): PhoneNumber | null => {
    if (!customer?.phone) return null;

    return parsePhoneNumber(customer.phone) ?? null;
  }, [customer?.phone]);

  const isValidPhoneNumber = useMemo(() => {
    if (phoneKnown) return true;
    if (!country || !phone) return false;

    return validatePhoneNumber(country, phone);
  }, [country, phone, phoneKnown]);

  const submitProfile = (
    data: FinishProfileFormType,
    { smsOptIn, onDone, onError }: SubmitProfileOptions
  ) => {
    updateCustomer(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        dob: requiresDob
          ? parse(data.dob, 'MM/dd/yyyy', new Date())
          : undefined,
        smsOptIn,
      },
      {
        onSuccess: () => {
          if (phoneKnown) {
            onDone();
            return;
          }

          const formattedPhone = `+${country?.callingCode}${phone}`;

          startCustomerOtp(
            { phone: formattedPhone },
            {
              onSuccess: () => onDone(formattedPhone),
              onError: (e) => onError(e.message),
            }
          );
        },
        onError: (e) => onError(e.message),
      }
    );
  };

  return {
    customer,
    control,
    trigger,
    setValue,
    handleSubmit,
    isValid,
    handleDobChange,
    phone,
    setPhone,
    country,
    setCountry,
    phoneKnown,
    parsedExistingPhone,
    isValidPhoneNumber,
    isSaving: isUpdatingCustomer || isStartingOtp,
    submitProfile,
  };
}
